import CountdownStrip from "./CountdownStrip";

const APP_URL = "/signup";

/**
 * Onboarding stepper — conveys how simple it is to get going:
 * Sign up → Add templates (optional) → Dictate → Report.
 * A connected, numbered 4-step flow in the natoe.ai design language.
 * Claims are grounded: Google/email sign-in, template import is optional,
 * dictate naturally, structured report in seconds. Browser-based, no install.
 */

const IconSignup = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M19 8v6M22 11h-6" />
  </svg>
);
const IconTemplate = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="3" width="16" height="18" rx="2" /><path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
);
const IconMic = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="2" width="6" height="11" rx="3" /><path d="M5 10a7 7 0 0 0 14 0M12 17v4" />
  </svg>
);
const IconReport = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 15l2 2 4-4" />
  </svg>
);
const IconIntegrate = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 7V3M15 7V3" /><path d="M6 7h12v3a6 6 0 0 1-12 0V7z" /><path d="M12 16v5" />
  </svg>
);
// First-N-reports-free trial. Change FREE_REPORTS to set the number.
const FREE_REPORTS = 100;

const STEPS = [
  { n: "1", Icon: IconSignup,    title: "Sign up",     body: "Create your account with Google or email." },
  { n: "2", Icon: IconTemplate,  title: "Templates",   body: "Bring your own, or let DXR handle it.", optional: true },
  { n: "3", Icon: IconMic,       title: "Dictate",     body: "Speak naturally — just the abnormals, in any order." },
  { n: "4", Icon: IconReport,    title: "Report",      body: "A structured, sign-off-ready report in seconds." },
  { n: "5", Icon: IconIntegrate, title: "Integration", body: "Fits right into your RIS, PACS or EHR workflow." },
];

export default function DxrOnboarding() {
  return (
    <section className="dob">
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <h2 className="dob-h2">30-second setup. <span className="dob-accent">70% faster</span> reports.</h2>

      <div className="dob-flow">
        {STEPS.map((s) => (
          <div key={s.title} className="dob-step">
            <span className="dob-ico">
              <s.Icon />
              <span className="dob-num">{s.n}</span>
            </span>
            <h3>
              {s.title}
              {s.optional && <span className="dob-opt">optional</span>}
            </h3>
            <p>{s.body}</p>
          </div>
        ))}
      </div>

      <CountdownStrip freeReports={FREE_REPORTS} appUrl={APP_URL} />
    </section>
  );
}

const styles = `
.dob { width: 90vw; margin: 0 auto; padding: 4% 0; font-family: 'Inter', sans-serif; text-align: center; }
.dob-h2 { font-size: clamp(26px, 3.6vw, 40px); font-weight: 700; color: #fff; letter-spacing: -0.02em; line-height: 1.2; margin: 0 0 44px; }
.dob-accent { color: #2997FF; }

/* Connected step flow — numbered nodes joined by a line to convey the sequence */
.dob-flow { position: relative; display: flex; justify-content: center; gap: clamp(16px, 3vw, 48px); }
.dob-flow::before { content: ''; position: absolute; top: 28px; left: 12%; right: 12%; height: 2px; background: linear-gradient(90deg, rgba(41,151,255,0.45), rgba(41,151,255,0.18)); z-index: 0; }
.dob-step { position: relative; z-index: 1; flex: 1 1 0; max-width: 300px; display: flex; flex-direction: column; align-items: center; }
.dob-ico { position: relative; width: 56px; height: 56px; border-radius: 16px; background: linear-gradient(135deg, rgba(41,151,255,0.18), rgba(0,16,157,0.18)); border: 1px solid rgba(41,151,255,0.3); color: #2997FF; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; box-shadow: 0 0 0 8px #05070d; }
.dob-num { position: absolute; top: -8px; right: -8px; width: 22px; height: 22px; border-radius: 50%; background: #2997FF; color: #00121f; font-size: 12px; font-weight: 800; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 0 3px #05070d; }
.dob-step h3 { font-size: 17px; font-weight: 600; color: #fff; margin: 0 0 6px; display: flex; align-items: center; gap: 8px; justify-content: center; flex-wrap: wrap; }
.dob-opt { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #2997FF; background: rgba(41,151,255,0.12); border-radius: 999px; padding: 2px 9px; }
.dob-step p { font-size: 14px; line-height: 1.55; color: rgba(255,255,255,0.65); margin: 0; max-width: 230px; }

/* Offer banner — three parts; refined, calm depth + a slow shine */
.dob-strip { position: relative; overflow: hidden; display: flex; flex-direction: row; flex-wrap: wrap; align-items: center; justify-content: center; gap: 18px 44px; margin: 36px auto 0; padding: 22px 30px; border-radius: 16px; background: linear-gradient(180deg, rgba(41,151,255,0.1), rgba(41,151,255,0.03)); border: 1px solid rgba(41,151,255,0.22); box-shadow: 0 12px 44px -18px rgba(41,151,255,0.5), inset 0 1px 0 rgba(255,255,255,0.06); }
.dob-strip::after { content: ''; position: absolute; top: 0; left: -150%; width: 60%; height: 100%; z-index: 0; pointer-events: none; background: linear-gradient(100deg, transparent, rgba(140,185,255,0.16), transparent); transform: skewX(-20deg); animation: dobStripShine 6s ease-in-out infinite; }
@keyframes dobStripShine { 0% { left: -150%; } 55%, 100% { left: 150%; } }
@media (prefers-reduced-motion: reduce) { .dob-strip::after { display: none; } }
.dob-strip-part { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 10px; text-align: center; }
/* thin vertical divider sitting in the gap between parts */
.dob-strip-part + .dob-strip-part::before { content: ''; position: absolute; left: -22px; top: 50%; transform: translateY(-50%); width: 1px; height: 52px; background: rgba(41,151,255,0.22); }
.dob-strip-eyebrow { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.1em; color: #2997FF; }
.dob-strip-timer { display: flex; flex-direction: column; align-items: center; gap: 5px; }
.dob-strip-ends { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: rgba(255,255,255,0.5); }
/* Center "credits free" is the hero — largest element, with "free" emphasized. */
.dob-strip-headline { font-size: clamp(25px, 3vw, 34px); font-weight: 700; line-height: 1.1; color: #fff; letter-spacing: -0.01em; }
.dob-strip-headline b { color: #2997FF; font-weight: 800; }
.dob-strip-note { font-size: 12px; font-weight: 500; color: rgba(255,255,255,0.55); }
.dob-strip-clock { display: flex; align-items: center; gap: 4px; font-variant-numeric: tabular-nums; }
.dob-strip-clock .t-unit { min-width: 34px; text-align: center; padding: 5px 7px; border-radius: 8px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); font-size: 18px; font-weight: 700; color: #fff; box-shadow: inset 0 1px 0 rgba(255,255,255,0.06); }
.dob-strip-clock i { font-style: normal; font-weight: 700; color: rgba(255,255,255,0.4); }
.dob-strip-signup { flex: none; }
.dob-btn { font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 600; padding: 12px 30px; border-radius: 999px; text-decoration: none; transition: background 200ms ease, color 200ms ease, border-color 200ms ease, transform 200ms ease; }
.dob-btn-primary { color: #00121f; background: #2997FF; border: 1px solid #2997FF; box-shadow: 0 8px 20px -8px rgba(41,151,255,0.7); }
.dob-btn-primary:hover { background: #1f86e8; border-color: #1f86e8; transform: translateY(-1px); }
.dob-btn-ghost { color: #fff; background: transparent; border: 1px solid rgba(255,255,255,0.3); }
.dob-btn-ghost:hover { border-color: #fff; }

@media (max-width: 760px) {
  .dob-flow { flex-direction: column; gap: 22px; align-items: stretch; }
  .dob-flow::before { display: none; }
  .dob-step { flex-direction: row; max-width: none; align-items: flex-start; text-align: left; gap: 16px; }
  .dob-ico { margin-bottom: 0; box-shadow: none; flex: none; }
  .dob-step h3 { justify-content: flex-start; }
  .dob-step p { max-width: none; }
  .dob-strip { flex-direction: column; justify-content: center; gap: 20px; }
  .dob-strip-part + .dob-strip-part::before { display: none; }
}
`;
